import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import InboxReorderDragLayer from "../../InboxReorderDragLayer";
import { Flex } from "@chakra-ui/react";
import InboxSubTaskToggle from "./InboxSubTaskToggle";
import InboxBlockBody from "./InboxBlockBody";
import { useInboxDragAndDrop } from "../../../../hooks/inbox/useInboxDragAndDrop";
import { BlockType } from "../../../../types/block/type";
import clsx from "clsx";
import { ContextMenu } from "radix-ui";
import { ContextMenuContent } from "../../../atom/ContextMenuContent";

interface TaskRowProps {
  data: BlockType;
  index: number;
  onDataDuplicate: (e: React.MouseEvent, rowData: BlockType) => void;
  onReorder: (payload: number[]) => void;
  isSelected: boolean;
  onSelect: (id: number | null) => void;
  isOverdue?: boolean;
  onDataDelete: (rowData: BlockType) => void;
  onClickBlock: (
    e: React.MouseEvent,
    data: BlockType,
    ref: React.RefObject<HTMLDivElement>
  ) => void;
  onClickCheckBox: (e: React.MouseEvent, data: BlockType) => void;
}
const TaskRow = React.memo(
  (props: TaskRowProps) => {
    const {
      data,
      index,
      isSelected,
      onClickBlock,
      onClickCheckBox,
      onDataDuplicate,
      onDataDelete,
      onReorder,
      onSelect,
    } = props;

    const [prevIndex, setPrevIndex] = useState(index);
    const [showAnimation, setShowAnimation] = useState(true);

    // 최초 마운트 시에만 애니메이션 동작하도록 추가
    useEffect(() => {
      setShowAnimation(false);
    }, []);

    const { ref, isDragging: shouldShowPreview } = useInboxDragAndDrop({
      index,
      data,
      onReorder,
      prevIndex,
      setPrevIndex,
    });

    const handleDelete = async () => {
      onDataDelete(data);
    };

    const handleDuplicate = (e: React.MouseEvent) => {
      onDataDuplicate(e, data);
    };

    return (
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          <div
            ref={ref}
            className={clsx(
              "box-border flex min-h-[30px] w-full cursor-pointer rounded-[5px] border border-solid",
              "hover:bg-w-4-hover-big",
              shouldShowPreview || isSelected ? "bg-w-4-hover-big" : "bg-initial",
              isSelected ? "border-w-8-hover" : "border-transparent",
              showAnimation && "animate-moba-fade-in",
              "data-[state=open]:bg-w-4-hover-big"
            )}
            id="inbox-general"
            onClick={(e) => onClickBlock(e, data, ref)}
            draggable="true"
            key={data.id}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Flex
              opacity={shouldShowPreview ? 0 : 1}
              w={"full"}
            >
              <InboxSubTaskToggle />
              <InboxBlockBody
                data={data}
                isOverdue={false}
                isCompleted={false}
                onClickCheckBox={onClickCheckBox}
              />
            </Flex>
            {shouldShowPreview &&
              createPortal(
                <Flex cursor="pointer">
                  <InboxReorderDragLayer />
                </Flex>,
                document.body
              )}
          </div>
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenuContent>
            <ContextMenuContent.Item onClick={handleDuplicate}>Duplicate</ContextMenuContent.Item>
            <ContextMenuContent.Item onClick={handleDelete}>Delete</ContextMenuContent.Item>
          </ContextMenuContent>
        </ContextMenu.Portal>
      </ContextMenu.Root>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.index === nextProps.index &&
      prevProps.isSelected === nextProps.isSelected
    );
  }
);

export default TaskRow;
